import { useId, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Access, AccessRule, AccessRuleType, EmployeeSubtype, EmployeeType } from '@prisma/client';
import {
  Breadcrumb,
  Button,
  Descriptions,
  Dropdown,
  Input,
  Layout,
  Modal,
  Space,
  Spin,
  Table,
  theme,
  Typography,
} from 'antd';
import { DescriptionsItemType } from 'antd/es/descriptions';
import { ColumnsType } from 'antd/es/table';

import { Role } from '@app/domain-interfaces';
import { Auth, PageLayout } from '@app/web-components';

import { EmployeeTypeRenderer } from '../../../../common';
import {
  accessRuleIncludes,
  AccessRuleTypeRenderer,
  AccessRuleWithDetail,
  useAccessRuleActions,
} from '../../../rules/libs';
import { useFindUniqueAccess } from '../../data';

export const employeeSubtypeOptions = [
  { text: EmployeeSubtype.ODC, value: EmployeeSubtype.ODC },
  { text: EmployeeSubtype.OFFSITE, value: EmployeeSubtype.OFFSITE },
  { text: EmployeeSubtype.VPN, value: EmployeeSubtype.VPN },
  { text: EmployeeSubtype.EDA, value: EmployeeSubtype.EDA },
];

const MANAGE_RULES_PAGE_TITLE = 'Manage Rules';

export type AccessWithRules = Access & {
  accessRules: AccessRuleWithDetail[];
  causingAccessConflicts: (AccessRule & { access: Access; conflictAccess: Access })[];
};

export const RulesPage = () => {
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const { id } = useParams();
  const accessId = id ? Number(id) : 0;
  const accessConflictHeaderId = useId();
  const [searchText, setSearchText] = useState('');
  const { user } = Auth.useUserAuth();

  const { data, isFetching } = useFindUniqueAccess<AccessWithRules>(
    {
      where: { id: accessId },
      include: {
        accessRules: {
          include: accessRuleIncludes,
        },
        causingAccessConflicts: {
          include: { access: true, conflictAccess: true, owners: { include: { user: true } } },
        },
      },
    },
    { enabled: accessId > 0 },
  );

  const { getCreateButtonProps, getDeleteModalProps, getDetailsModalProps, getDropdownButtonProps } =
    useAccessRuleActions({
      onCreate: () => './create',
      onEdit: (record) => `/admin/access-rules/${record.id}`,
    });

  const columns: ColumnsType<AccessRuleWithDetail> = [
    {
      title: 'Action',
      width: 120,
      render: (_, record) => {
        const { menu, ...otherProps } = getDropdownButtonProps(record);
        return menu?.items && menu.items.length > 0 ? (
          <Dropdown.Button menu={menu} {...otherProps} />
        ) : (
          <Button {...otherProps} />
        );
      },
    },
    {
      title: 'Rule Type',
      dataIndex: ['conflict', 'ruleType'],
      filters: [
        { text: 'Access Conflict', value: AccessRuleType.ACCESS_CONFLICT },
        { text: 'Department Conflict', value: AccessRuleType.DEPARTMENT_CONFLICT },
      ],
      filterMultiple: false,
      width: 225,
      onFilter: (value, record) => record.conflict.ruleType === value,
      sorter: (a, b) => a.conflict.ruleType.localeCompare(b.conflict.ruleType),
      render: AccessRuleTypeRenderer,
    },
    {
      title: 'Employee Type',
      width: 225,
      dataIndex: 'employeeType',
      filters: [
        { text: 'Intel', value: EmployeeType.INTEL },
        { text: 'CW', value: EmployeeType.CONTINGENT_WORKER },
      ],
      onFilter: (value, record) => record.employeeType === value,
      render: (employeeType: EmployeeType) => <EmployeeTypeRenderer employeeType={employeeType} />,
      sorter: (a, b) => a.employeeType.localeCompare(b.employeeType),
    },
    {
      title: 'Employee Subtype',
      width: 225,
      dataIndex: 'employeeSubtype',
      filters: employeeSubtypeOptions,
      onFilter: (value, record) => record.employeeSubtype === value,
      sorter: (a, b) => (a.employeeSubtype ?? '').localeCompare(b.employeeSubtype ?? ''),
    },
    {
      title: 'Conflict Name',
      dataIndex: ['conflict', 'name'],
      sorter: (a, b) => a.conflict.name.localeCompare(b.conflict.name),
    },
  ];

  if (isFetching || !data) {
    return <Spin></Spin>;
  }

  const items: DescriptionsItemType[] = [
    { key: 'access', children: data.name, label: 'Name' },
    { key: 'source', children: data.accessSource, label: 'Source' },
    { key: 'type', children: data.employeeType, label: 'Employee Type' },
  ];

  return (
    <>
      <Modal {...getDetailsModalProps()} />
      <Modal {...getDeleteModalProps()} />
      <Breadcrumb
        style={{ margin: '16px 0' }}
        items={[
          { title: <Link to="/">Home</Link> },
          { title: <Link to="../">Access</Link> },
          { title: MANAGE_RULES_PAGE_TITLE },
        ]}
      />
      <Layout.Content
        style={{
          padding: 24,
          margin: 'auto',
          height: '100%',
          width: '100%',
          minHeight: 0,
          background: colorBgContainer,
          overflow: 'auto',
        }}
      >
        <PageLayout.Title title={MANAGE_RULES_PAGE_TITLE} />
        <Descriptions title="Access Info" items={items} />
        <Typography.Title id={accessConflictHeaderId} level={5}>
          Access Rules
        </Typography.Title>
        <Space style={{ marginBottom: '1em' }}>
          <Input
            aria-label="Search By Conflict Name"
            placeholder="Search By Conflict Name"
            style={{ width: 350 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          {user?.roles[Role.RULE_ADMIN] ? (
            <>
              <Button {...getCreateButtonProps(AccessRuleType.ACCESS_CONFLICT)} />
              <Button {...getCreateButtonProps(AccessRuleType.DEPARTMENT_CONFLICT)} />
            </>
          ) : undefined}
        </Space>
        <Table
          aria-labelledby={accessConflictHeaderId}
          rowKey={(record) => `${record.id}-${record.accessId}-${record.conflictAccessId}`}
          columns={columns}
          dataSource={[
            ...data.accessRules,
            ...(data.causingAccessConflicts.map((x) => ({
              ...x,
              accessId: x.conflictAccessId,
              conflictAccessId: x.accessId,
              access: x.conflictAccess,
              conflict: { id: x.id, name: x.access.name, ruleType: AccessRuleType.ACCESS_CONFLICT },
            })) as unknown as AccessRuleWithDetail[]),
          ].filter((x) => !searchText || x.conflict.name.toLowerCase().includes(searchText.toLowerCase()))}
          pagination={false}
          size="small"
        />
      </Layout.Content>
    </>
  );
};
